import { Injectable, NgZone } from '@angular/core';
import { ReadingActions } from '../../core/enums';
import { TtsService } from '../speech/tts.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ReadingAtom, SpeakAtom } from '../../core/reading-utils/interfaces';
import { SettingsService } from '../settings/settings.service';
import { AsgardDocBase } from 'src/app/core/documents/asgard-doc-base';
import { TabService } from '../tab.service';
import { ISettings } from '../../core/interfaces';
import { DocumentManagerService } from '../document/document-manager.service';
import { ReadingModesService } from '../reading-modes.service';
import * as Enms from '../../core/reading-utils/enums';
import {SpeakingJobType} from '../../core/reading-utils/enums';
import { CurrentUiStoreService } from '../ui/current-ui-store.service';
import { AsgardFeatureNames } from 'src/models/AsgardFeatures';

@Injectable({
  providedIn: 'root'
})
export class ReadingService {
  private settings: ISettings | null = null;
  private readonly readingStatusBhvSubj: BehaviorSubject<Enms.ReaderStatus> = new BehaviorSubject(Enms.ReaderStatus.Stopped);

  get readingStatusObservable(): Observable<Enms.ReaderStatus> { return this.readingStatusBhvSubj.asObservable(); }
  get readingStatus(): Enms.ReaderStatus { return this.readingStatusBhvSubj.getValue(); }

  get activeSpeakingJob(): SpeakingJobType | null {
    return this.ttsService.activeJobType;
  }

  private get currentDoc(): AsgardDocBase | null | undefined { return this.docMgrService.currentDoc; }

  private get selectedVoice(): string | null {
    return this.tabService.activeTab !== null ?
      this.tabService.getTabVoice(this.tabService.activeTab.id) : this.settings ? this.settings.savedVoice : null;
  }

  private get selectedSpeed(): number {
    if (this.tabService.activeTab) {
      const tabSpeed = this.tabService.getTabReadingSpeed(this.tabService.activeTab.id);
      
      if (tabSpeed !== null) 
        return tabSpeed;
    }

    return this.settings ? this.settings.speed : 0;
  }

  private get currentItem(): ReadingAtom[] | null {
    return this.currentDoc ? this.currentDoc.readingItem : null;
  }

  private get canReadNextItem(): boolean {
    return this.currentDoc ? this.settings !== null && this.currentDoc.hasNextItemToRead(this.settings.readingUnit) : false;
  }

  private get continuousReading(): boolean {
    return this.settings !== null &&
      (this.readingModeService.readingBase !== Enms.ReadingBase.Continuous ||
        this.readingModeService.readingMode === Enms.ReadingMode.RectangleSelection ||
        this.readingModeService.readingMode === Enms.ReadingMode.Hover);
  }

  private get locked(): boolean {
    return this.uiService.uiState.value[AsgardFeatureNames.SPEECH].locked;
  }

  constructor(
    private readonly ttsService: TtsService,
    private readonly tabService: TabService,
    private readonly settingsService: SettingsService,
    private readonly docMgrService: DocumentManagerService,
    private readonly readingModeService: ReadingModesService,
    private readonly ngZone: NgZone,
    private readonly uiService: CurrentUiStoreService
  ) {
    this.initSubscriptions();
  }

  runAction(action: ReadingActions): void {
    if (action === ReadingActions.PlayPause) {
      switch (this.readingStatusBhvSubj.getValue()) {
        case Enms.ReaderStatus.Stopped:
          this.speakCurrentItem();
          break;
        case Enms.ReaderStatus.Paused:
          this.ttsService.resume();
          break;
        case Enms.ReaderStatus.Speaking:
          this.ttsService.pause();
          break;
        default: break;
      }
    } else if (action === ReadingActions.MoveForward || action === ReadingActions.MoveBackward) {
      if (this.readingStatusBhvSubj.getValue() !== Enms.ReaderStatus.Stopped) {
        this.stop();
      }

      this.moveReadingPos(action === ReadingActions.MoveForward);

    } else if (action === ReadingActions.Stop) {
      this.stop();
    }
  }

  stop(): void {
    this.changeReadingStatus(Enms.ReaderStatus.Stopped);
    this.ttsService.stop();
  }

  readSimpleText(text: string): void {
    if (!this.selectedVoice) {
      return;
    }

    if (this.locked) {
      return;
    }

    this.stop();
    
    const speakAtoms = this.generateSpeakAtoms(text);
    this.ttsService.speak(speakAtoms, Enms.SpeakingJobType.SimpleText, this.selectedVoice, this.selectedSpeed);
  }

  readTooltip(text: string) {
    const voice = this.settings?.UIVoice;

    if (voice === undefined) {
      return;
    }

    if (this.locked) {
      return;
    }

    this.stop();

    const speakAtoms = this.generateSpeakAtoms(text);
    this.ttsService.speak(speakAtoms, Enms.SpeakingJobType.Tooltip, voice, this.selectedSpeed);
  }

  readMenuItem(text: string, voice: string) {
    if (!!!voice) {
      return;
    }

    if (this.locked) {
      return;
    }

    this.stop();

    const speakAtoms = this.generateSpeakAtoms(text);
    this.ttsService.speak(speakAtoms, Enms.SpeakingJobType.MenuItem, voice, this.selectedSpeed);
  }

  generateSpeakAtoms(text: string): SpeakAtom[] {
    const speakAtoms = text.split(' ').filter(x => x.trim() !== '').map((val, idx) => (
      { symbPos: 0, itemIdx: idx, text: val, lang: null, itemType: Enms.TxtItemType.Word, sntncIndex: -1 })
    );

    let symbPos = 0;
    speakAtoms.forEach(x => {
      x.symbPos = symbPos;
      symbPos += x.text.length + 1;
    });
    
    return speakAtoms;
  }

  private async moveReadingPos(isForwardMove: boolean = true): Promise<void> {
    if (this.currentDoc === null) {
      return;
    }

    if (this.currentDoc && this.settings) {
      if (isForwardMove) {
        await this.currentDoc.goToNextReadingItem(this.settings.readingUnit);
      } else {
        await this.currentDoc.goToPrevReadingItem(this.settings.readingUnit);
      }
    }
  }

  private async speakCurrentItem(): Promise<void> {
    if (this.locked) {
      return;
    }
    if (!this.currentDoc) {
      return;
    }

    if (this.currentDoc.hasReadingItem && this.currentItem && this.selectedVoice) {
      const speakItems = this.currentItem.map(x => (
        { symbPos: x.symbPos, itemIdx: x.itemIdx, text: x.text, lang: x.lang, itemType: x.itemType, sntncIndex: x.sntncIndex }
      ));
      this.ttsService.speak(speakItems, await this.currentDoc.getSpeakingJobType(), this.selectedVoice, this.selectedSpeed);
    } else {
      this.stop();
    }
  }

  private changeReadingStatus(newStatus: Enms.ReaderStatus): void {
    if (this.readingStatusBhvSubj.getValue() !== newStatus) {
      this.ngZone.run(() => this.readingStatusBhvSubj.next(newStatus));
    }
  }

  private initSubscriptions(): void {
    this.ttsService.soundStatus.subscribe(x => {
      switch (x) {
        case Enms.SoundStatus.Stopped:
          if (this.readingStatusBhvSubj.getValue() !== Enms.ReaderStatus.Stopped) {
            if (this.ttsService.activeJobType !== Enms.SpeakingJobType.Document) { // just stop if it's not a document
              this.changeReadingStatus(Enms.ReaderStatus.Stopped);
            } else if (!this.continuousReading || !this.canReadNextItem) { // for document check if continue reading possible
              this.changeReadingStatus(Enms.ReaderStatus.Stopped);
            }
          }
          break;
        case Enms.SoundStatus.WaitingForSound:
        case Enms.SoundStatus.Playing:
          this.changeReadingStatus(Enms.ReaderStatus.Speaking);
          break;
        case Enms.SoundStatus.Paused:
          this.changeReadingStatus(Enms.ReaderStatus.Paused);
          break;
        case Enms.SoundStatus.Error:
          this.changeReadingStatus(Enms.ReaderStatus.Stopped);
          break;
        default: return;
      }
    });

    this.ttsService.readingPosition.subscribe(msg => {
      if (msg === null) {
        return;
      }

      if (msg.boundaryType === Enms.ReadingBoundary.Text) {
        const readingMode = this.readingModeService.readingMode;
        const readingBase = this.readingModeService.readingBase;

        if ((this.ttsService.activeJobType !== Enms.SpeakingJobType.Document 
          && this.ttsService.activeJobType !== SpeakingJobType.AltText
          && this.ttsService.activeJobType !== SpeakingJobType.Link) // only 'Document' and 'link' job type support continuous reading
          || readingMode === Enms.ReadingMode.Selection) // selection mode doesn't support continuous reading
        { 
          return;
        }

        const isCurrItmFinished = msg.batchFinished || this.currentItem && this.currentItem.length === 0;
        if (isCurrItmFinished) {
          if (readingBase === Enms.ReadingBase.Continuous 
          && this.canReadNextItem 
          && this.ttsService.activeJobType !== SpeakingJobType.Link
          && (
            readingMode === Enms.ReadingMode.ClickToPlay ||
            readingMode === Enms.ReadingMode.ClickToRead
          )
        ) {
          this.moveReadingPos(true).then(() => this.speakCurrentItem());
        } else if (readingBase === Enms.ReadingBase.Manual || this.ttsService.activeJobType === SpeakingJobType.Link) {
          this.changeReadingStatus(Enms.ReaderStatus.Stopped);
          this.moveReadingPos(true);
        }
        }
      }
    });

    this.settingsService.getSettingsObservable().subscribe((i: ISettings) => {
      if (this.readingStatusBhvSubj.getValue() === Enms.ReaderStatus.Speaking) {
        this.ttsService.pause();
      }

      if (this.settings !== null) {
        this.stop();
      }

      this.settings = i;
    });

    this.tabService.activeTabObservable().subscribe(tab => {
      if (tab) {
        this.stop();
      }
    });
  }
}
